import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { NotificationService } from "@/gen/omnios/v1/notification_connect";
import type {
	WebhookEventType,
	WebhookSubscription,
} from "@/gen/omnios/v1/notification_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AlertCircle,
	Check,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Copy,
	Eye,
	Loader2,
	Plus,
	Send,
	Trash2,
	Webhook,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/webhooks")({
	component: WebhooksPage,
});

const client = createClient(NotificationService, transport);

function WebhooksPage() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("webhooks_breadcrumb") },
	]);

	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);

	const { data: webhooksResp, isLoading } = useQuery({
		queryKey: ["webhooks"],
		queryFn: () => client.listWebhooks({}),
	});
	const webhooks = webhooksResp?.webhooks ?? [];

	const { data: eventTypesResp } = useQuery({
		queryKey: ["webhook-event-types"],
		queryFn: () => client.listWebhookEventTypes({}),
	});
	const eventTypes = eventTypesResp?.eventTypes ?? [];

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteWebhook({ id }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
	});

	const toggleMut = useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			client.updateWebhook({ id, isActive }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
	});

	const testMut = useMutation({
		mutationFn: (id: string) => client.testWebhook({ id }),
	});

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm text-muted-foreground">{t("webhooks_desc")}</p>
				</div>
				<Dialog open={showCreate} onOpenChange={setShowCreate}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-4 w-4 mr-1" />
							{t("webhooks_create")}
						</Button>
					</DialogTrigger>
					<CreateWebhookDialog
						eventTypes={eventTypes}
						onCreated={() => {
							setShowCreate(false);
							qc.invalidateQueries({ queryKey: ["webhooks"] });
						}}
					/>
				</Dialog>
			</div>

			{webhooks.length === 0 ? (
				<Card className="border-dashed">
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<Webhook className="h-12 w-12 text-muted-foreground/50" />
						<h3 className="mt-4 text-lg font-semibold">
							{t("webhooks_empty")}
						</h3>
						<p className="mt-2 text-sm text-muted-foreground">
							{t("webhooks_empty_hint")}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-3">
					{webhooks.map((wh) => (
						<WebhookCard
							key={wh.id}
							webhook={wh}
							onToggle={(active) =>
								toggleMut.mutate({ id: wh.id, isActive: active })
							}
							onDelete={() => deleteMut.mutate(wh.id)}
							onTest={() => testMut.mutate(wh.id)}
							testResult={
								testMut.variables === wh.id ? testMut.data : undefined
							}
							testPending={testMut.isPending && testMut.variables === wh.id}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function WebhookCard({
	webhook,
	onToggle,
	onDelete,
	onTest,
	testResult,
	testPending,
}: {
	webhook: WebhookSubscription;
	onToggle: (active: boolean) => void;
	onDelete: () => void;
	onTest: () => void;
	testResult?: { responseStatus: number; success: boolean };
	testPending: boolean;
}) {
	const { t } = useTranslation("settings");
	const [expanded, setExpanded] = useState(false);
	const [secretVisible, setSecretVisible] = useState(false);
	const [copied, setCopied] = useState(false);

	const copySecret = () => {
		navigator.clipboard.writeText(webhook.secret);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Card className="overflow-hidden">
			<div className="flex items-center gap-3 px-4 py-3">
				<Switch checked={webhook.isActive} onCheckedChange={onToggle} />
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="font-medium text-sm">{webhook.name}</span>
						<Badge
							variant={webhook.isActive ? "default" : "secondary"}
							className="text-[10px]"
						>
							{webhook.isActive ? t("webhooks_active") : t("webhooks_disabled")}
						</Badge>
					</div>
					<p className="text-xs text-muted-foreground truncate">
						{webhook.url}
					</p>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={onTest}
						disabled={testPending}
						title={t("webhooks_test")}
					>
						{testPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setExpanded(!expanded)}
					>
						{expanded ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronRight className="h-4 w-4" />
						)}
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="ghost" size="sm" className="text-destructive">
								<Trash2 className="h-4 w-4" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t("webhooks_delete_title")}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("webhooks_delete_desc", { name: webhook.name })}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
								<AlertDialogAction
									onClick={onDelete}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{t("common:delete")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			{testResult && (
				<div
					className={cn(
						"px-4 pb-2 text-xs flex items-center gap-1",
						testResult.success ? "text-green-600" : "text-destructive",
					)}
				>
					{testResult.success ? (
						<CheckCircle2 className="h-3 w-3" />
					) : (
						<AlertCircle className="h-3 w-3" />
					)}
					HTTP {testResult.responseStatus}
				</div>
			)}

			{expanded && (
				<div className="border-t px-4 py-3 space-y-3 bg-muted/30">
					<div>
						<span className="text-xs text-muted-foreground">
							{t("webhooks_events_label")}:{" "}
						</span>
						<div className="flex flex-wrap gap-1 mt-1">
							{webhook.events.map((e) => (
								<Badge key={e} variant="outline" className="text-[10px]">
									{e}
								</Badge>
							))}
						</div>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-xs text-muted-foreground">
							{t("webhooks_secret")}:{" "}
						</span>
						<code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
							{secretVisible ? webhook.secret : "••••••••••••••••"}
						</code>
						<Button
							variant="ghost"
							size="sm"
							className="h-6 w-6 p-0"
							onClick={() => setSecretVisible(!secretVisible)}
						>
							<Eye className="h-3 w-3" />
						</Button>
						<Button
							variant="ghost"
							size="sm"
							className="h-6 w-6 p-0"
							onClick={copySecret}
						>
							{copied ? (
								<Check className="h-3 w-3 text-green-600" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					</div>
				</div>
			)}
		</Card>
	);
}

function CreateWebhookDialog({
	eventTypes,
	onCreated,
}: {
	eventTypes: WebhookEventType[];
	onCreated: () => void;
}) {
	const { t } = useTranslation("settings");
	const [name, setName] = useState("");
	const [url, setUrl] = useState("");
	const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());

	const createMut = useMutation({
		mutationFn: () =>
			client.createWebhook({
				name,
				url,
				events: [...selectedEvents],
			}),
		onSuccess: onCreated,
	});

	const grouped = eventTypes.reduce(
		(acc, et) => {
			const cat = et.category || t("webhooks_category_other");
			if (!acc[cat]) acc[cat] = [];
			acc[cat].push(et);
			return acc;
		},
		{} as Record<string, WebhookEventType[]>,
	);

	const toggleEvent = (event: string) => {
		setSelectedEvents((prev) => {
			const next = new Set(prev);
			if (next.has(event)) next.delete(event);
			else next.add(event);
			return next;
		});
	};

	const isValidUrl = url.startsWith("http://") || url.startsWith("https://");

	return (
		<DialogContent className="max-w-lg">
			<DialogHeader>
				<DialogTitle>{t("webhooks_new")}</DialogTitle>
			</DialogHeader>
			<div className="space-y-4">
				<div className="space-y-2">
					<Label>{t("common:name")}</Label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("webhooks_name_placeholder")}
					/>
				</div>
				<div className="space-y-2">
					<Label>URL</Label>
					<Input
						value={url}
						onChange={(e) => setUrl(e.target.value)}
						placeholder="https://example.com/webhooks"
					/>
					{url && !isValidUrl && (
						<p className="text-xs text-destructive">
							{t("webhooks_url_error")}
						</p>
					)}
				</div>
				<div className="space-y-2">
					<Label>
						{t("webhooks_events_select")}{" "}
						<span className="text-muted-foreground font-normal">
							({selectedEvents.size})
						</span>
					</Label>
					<div className="border rounded-md max-h-60 overflow-y-auto">
						<div className="p-2">
							<Label className="flex items-center gap-2 mb-2 cursor-pointer">
								<Checkbox
									checked={selectedEvents.has("*")}
									onCheckedChange={() => toggleEvent("*")}
								/>
								<span className="font-medium">{t("webhooks_all_events")}</span>
							</Label>
							{Object.entries(grouped).map(([category, events]) => (
								<div key={category} className="mb-2">
									<div className="text-xs font-medium text-muted-foreground px-1 mb-1">
										{category}
									</div>
									{events.map((et) => (
										<label
											key={et.event}
											className="flex items-center gap-2 text-sm py-0.5 px-1 cursor-pointer hover:bg-muted/50 rounded"
										>
											<Checkbox
												checked={
													selectedEvents.has(et.event) ||
													selectedEvents.has("*")
												}
												onCheckedChange={() => toggleEvent(et.event)}
												disabled={selectedEvents.has("*")}
											/>
											<span>{et.label}</span>
											<code className="text-[10px] text-muted-foreground ml-auto">
												{et.event}
											</code>
										</label>
									))}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			<DialogFooter>
				<Button
					onClick={() => createMut.mutate()}
					disabled={
						!name.trim() ||
						!isValidUrl ||
						selectedEvents.size === 0 ||
						createMut.isPending
					}
				>
					{createMut.isPending && (
						<Loader2 className="h-4 w-4 animate-spin mr-1" />
					)}
					{t("common:create")}
				</Button>
			</DialogFooter>
		</DialogContent>
	);
}
